/**
 * One <img> per URL (image or mask) for the placeholders of image patterns (origin and replicas): each one loading its
 *  own copy means N parses of the same data URL and N onloads, which reach the screen one by one (seconds with 100+
 *  replicas). Build and sync already share the origin's elements the same way. Everything else keeps loading its own.
 */

interface SharedImage {
    image: HTMLImageElement;
    loaded: Promise<HTMLImageElement>;
}

// A template uses a few (test image, default image, mask); the steps reload them, so a loaded one is reused right away.
//  Kept small: a test image can be a data URL of tens of MB, decoding to hundreds, and an evicted one is only loaded again
export const MAX_SHARED_IMAGES = 4;

const images = new Map<string, SharedImage>();

/** The element is returned right away with its src set, like a fresh <img>; `loaded` resolves on its onload */
export const sharedPatternImage = (url: string): SharedImage => {
    const known = images.get(url);
    if (known) {
        images.delete(url);
        images.set(url, known);
        return known;
    }
    const image = document.createElement('img');
    const loaded = new Promise<HTMLImageElement>((resolve, reject) => {
        image.onload = () => resolve(image);
        image.onerror = reject;
    });
    // A failed load isn't kept: the next request tries again
    loaded.catch(() => images.get(url)?.image === image && images.delete(url));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
    const entry = { image, loaded };
    images.set(url, entry);
    if (images.size > MAX_SHARED_IMAGES) images.delete(images.keys().next().value as string);
    return entry;
};

/** For tests */
export const clearPatternImages = () => images.clear();
